import { z } from 'zod/v4';

// A comma-separated id list collapses to a string[]; empty/blank → undefined so
// downstream filters can treat "absent" and "empty" identically.
const csvIds = z
  .string()
  .optional()
  .transform((value) =>
    value
      ? value
          .split(',')
          .map((v) => v.trim())
          .filter(Boolean)
      : undefined,
  );

// The owner(s) the report is scoped to, as a comma-separated id list. A single
// id drives the owner detail page; the Cross-Owner Funding view (Spec 3) reads
// the same layer with many ids at once — hence `IN (...)`, never `= id`.
const ownerIdsCsv = z
  .string()
  .transform((value) =>
    value
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean),
  )
  .refine((ids) => ids.length > 0, { message: 'ownerIds is required' });

export const ownerFundingSummaryQuerySchema = z.object({
  ownerIds: ownerIdsCsv,
  // Narrow the whole report to one or more normalized vendors (the vendor-share
  // "pick a vendor" mode). Absent = every vendor that bid for these owners.
  vendorIds: csvIds,
  // How many vendors to surface individually in the share view before the rest
  // roll up into "Others". Defaults to the spec's top-5.
  topVendors: z.coerce.number().int().min(1).max(100).optional(),
  // Profile-style report filters — comma-separated ids; `bidSizes` is the
  // encoded min-max window list ("0-5000000,100000000-").
  districts: csvIds,
  counties: csvIds,
  categories: csvIds,
  workTypes: csvIds,
  contractTypes: csvIds,
  projectTypes: csvIds,
  bidSizes: z.string().optional(),
});

export type OwnerFundingSummaryQuery = z.infer<
  typeof ownerFundingSummaryQuerySchema
>;

// Vendor filter options for the report — every normalized vendor that has
// awarded work for the owner(s), independent of the active vendor filter.
export const ownerFundingVendorsQuerySchema = z.object({
  ownerIds: ownerIdsCsv,
});

export type OwnerFundingVendorsQuery = z.infer<
  typeof ownerFundingVendorsQuerySchema
>;

// Cross-Owner Funding (EDG-912) — the same report laid across many owners at
// once. `ownerIds` is the full selected set; `groupBy` decides how they fold
// into comparison "units": one per owner, or one per state (every owner sharing
// a state aggregated together). All other params are the summary report's
// filters, applied identically to every unit (one synchronized filter set).
export const ownerFundingComparisonQuerySchema = z.object({
  ownerIds: ownerIdsCsv,
  groupBy: z.enum(['owner', 'state']).default('owner'),
  vendorIds: csvIds,
  topVendors: z.coerce.number().int().min(1).max(100).optional(),
  districts: csvIds,
  counties: csvIds,
  categories: csvIds,
  workTypes: csvIds,
  contractTypes: csvIds,
  projectTypes: csvIds,
  bidSizes: z.string().optional(),
});

export type OwnerFundingComparisonQuery = z.infer<
  typeof ownerFundingComparisonQuerySchema
>;
